"use client";

// getDisplayMedia MUST be called from a user-gesture handler — browsers reject it outside one.
// System audio echo risk: recording a Zoom call with mic enabled may double-capture the user's
// voice (screen audio + mic). Users should mute system audio in the browser dialog. Angular parity.
// Sentry Replay is 0% sample rate; if ever enabled, add a canvas block rule for /upload/record.

import { useRef, useCallback, useState, useEffect } from "react";
import type { RecordingEngineReturn } from "./use-recording-engine";
import { MAX_RECORDING_SECONDS } from "./use-recording-engine";
import type { RecordingEngineOptions } from "../types";
import { getRecordingErrorMessage } from "../utils/error-messages";
import type { ScreenShareComposition } from "../utils/screen-share-composer-imperative";
import { createScreenShareCompositionImperative } from "../utils/screen-share-composer-imperative";
import { getSupportedScreenMime } from "../utils/mime-negotiation";
import { createOnDataAvailable } from "../utils/recorder-handlers";

export interface UseScreenRecordingOptions extends RecordingEngineOptions {
  withCameraOverlay?: boolean;
  micConstraints?: MediaTrackConstraints;
  onDataAvailable?: (data: ArrayBuffer) => void;
  onAutoStop?: () => void;
  onUserCancel?: () => void;
  /** When true, AbortError from getDisplayMedia surfaces via engine.setError instead of silently calling onUserCancel. Default false. */
  emitErrorOnCancel?: boolean;
}

export interface UseScreenRecordingReturn extends RecordingEngineReturn {
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  isScreenCaptureActive: boolean;
  startScreenCapture: () => Promise<void>;
  stopScreenCapture: () => void;
}

const MIC_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
};

export function useScreenRecording(
  engine: RecordingEngineReturn,
  isActive: boolean,
  options?: UseScreenRecordingOptions,
): UseScreenRecordingReturn {
  const [isScreenCaptureActive, setIsScreenCaptureActive] = useState(false);

  // Guards double-stop race: screen track "ended" can fire async after stopScreenCapture() already ran.
  const stoppedRef = useRef(false);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const compositionRef = useRef<ScreenShareComposition | null>(null);

  useEffect(() => {
    return () => {
      cleanupStreams();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function cleanupStreams() {
    if (compositionRef.current) {
      compositionRef.current.stop();
      compositionRef.current = null;
    }
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((t) => t.stop());
      screenStreamRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => t.stop());
      micStreamRef.current = null;
    }
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((t) => t.stop());
      cameraStreamRef.current = null;
    }
  }

  const stopScreenCapture = useCallback(() => {
    if (stoppedRef.current) return; // idempotency guard
    stoppedRef.current = true;

    engine.stopMicHealthMonitor();

    if (
      engine.recorderRef.current &&
      engine.recorderRef.current.state !== "inactive"
    ) {
      engine.recorderRef.current.stop();
    }
    engine.stopTimer();
    cleanupStreams();

    if (engine.streamRef.current) {
      engine.streamRef.current.getTracks().forEach((t) => t.stop());
      engine.streamRef.current = null;
    }

    engine.setActiveStream(null);
    setIsScreenCaptureActive(false);
  }, [engine]);

  const startScreenCapture = useCallback(async () => {
    if (!isActive) return;

    stoppedRef.current = false;

    try {
      // aspectRatio 16:9 normalizes geometry across monitor sizes.
      // displaySurface intentionally omitted — Firefox rejects that constraint.
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 30 }, aspectRatio: 1.7777778 },
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });

      screenStreamRef.current = screenStream;

      engine.setScreenShareTrackCounts({
        audio: screenStream.getAudioTracks().length,
        video: screenStream.getVideoTracks().length,
      });

      let micStream: MediaStream | null = null;
      let cameraStream: MediaStream | null = null;

      if (options?.withCameraOverlay) {
        // Camera stream carries mic audio; 320×240 is the PiP overlay size.
        try {
          cameraStream = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 },
            video: { width: { ideal: 320 }, height: { ideal: 240 } },
          });
          cameraStreamRef.current = cameraStream;
        } catch (camErr) {
          // Camera (+ mic) failed — degrade to screen-only.
          const cameraErrMsg = getRecordingErrorMessage(camErr, false, "screen");
          if (cameraErrMsg !== null) {
            engine.setError(cameraErrMsg);
          }
        }
      } else {
        // Attempt mic audio; if it fails proceed with screen + system audio only.
        try {
          micStream = await navigator.mediaDevices.getUserMedia({
            audio: options?.micConstraints ?? MIC_CONSTRAINTS,
          });
          micStreamRef.current = micStream;
        } catch {
          // Mic acquisition failed — continue without mic.
        }
      }

      const screenVideoTrack = screenStream.getVideoTracks()[0];
      if (screenVideoTrack) {
        screenVideoTrack.addEventListener("ended", () => {
          if (!stoppedRef.current) {
            options?.onAutoStop?.();
            stopScreenCapture();
          }
        });
      }

      let recordingStream: MediaStream;

      if (cameraStream) {
        const composition = createScreenShareCompositionImperative({
          screenStream,
          cameraStream,
          micStream,
          position: "BR",
          size: "M",
        });
        compositionRef.current = composition;
        recordingStream = composition.stream;
      } else {
        // No overlay — merge screen video + screen audio + mic audio.
        recordingStream = new MediaStream([
          ...screenStream.getVideoTracks(),
          ...screenStream.getAudioTracks(),
          ...(micStream?.getAudioTracks() ?? []),
        ]);
      }

      engine.streamRef.current = recordingStream;
      engine.chunksRef.current = [];
      engine.setActiveStream(recordingStream);

      const mimeType = getSupportedScreenMime();
      const recorderOptions: MediaRecorderOptions = mimeType
        ? { mimeType }
        : {};

      const recorder = new MediaRecorder(recordingStream, recorderOptions);
      engine.recorderRef.current = recorder;

      recorder.ondataavailable = createOnDataAvailable(engine.chunksRef, "screen", options?.onDataAvailable);

      recorder.onstop = () => {
        engine.stopMicHealthMonitor();
        engine.stopTimer();

        compositionRef.current?.stop();
        compositionRef.current = null;

        // Stop all source stream tracks (camera LED off, mic LED off, screen released).
        screenStreamRef.current?.getTracks().forEach((t) => t.stop());
        cameraStreamRef.current?.getTracks().forEach((t) => t.stop());
        micStreamRef.current?.getTracks().forEach((t) => t.stop());
        screenStreamRef.current = null;
        cameraStreamRef.current = null;
        micStreamRef.current = null;

        engine.streamRef.current = null;
        engine.setActiveStream(null);
        setIsScreenCaptureActive(false);
        void engine.completeRecording(mimeType || "video/webm");
      };

      recorder.start(1000); // 1s timeslices
      engine.startTake("screen", mimeType || "video/webm");
      engine.startTimer();

      // Screen mode does not auto-stop on transient track inactivity — system audio mute or
      // window-switch should not abort the recording.
      engine.startMicHealthMonitor(recordingStream, {
        videoTracks: true,
        secondaryStream: cameraStream,
      });

      setIsScreenCaptureActive(true);

      // MAX_RECORDING_SECONDS auto-stop is driven exclusively by engine.startTimer.
      // Do NOT add a redundant setTimeout — double-stop throws InvalidStateError.
      void MAX_RECORDING_SECONDS;
    } catch (err) {
      // AbortError in screen mode = user dismissed picker → null → silent by default.
      const mapped = getRecordingErrorMessage(err, false, "screen");
      if (mapped === null) {
        if (options?.emitErrorOnCancel === true) {
          engine.setError("Screen capture was aborted. Please try again.");
        } else {
          options?.onUserCancel?.();
        }
      } else {
        engine.setError(mapped);
      }
      stoppedRef.current = true;
    }
  }, [isActive, engine, options, stopScreenCapture]);

  const startRecording = startScreenCapture;
  const stopRecording = stopScreenCapture;

  if (!isActive) {
    return {
      ...engine,
      startRecording: async () => {},
      stopRecording: () => {},
      isScreenCaptureActive: false,
      startScreenCapture: async () => {},
      stopScreenCapture: () => {},
    };
  }

  return {
    ...engine,
    startRecording,
    stopRecording,
    isScreenCaptureActive,
    startScreenCapture,
    stopScreenCapture,
  };
}

export type { UseScreenRecordingOptions as ScreenRecordingOptions };
